import { Box, Icon, Text } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { FiCircle, FiClock } from "react-icons/fi";
import { IoCheckmarkCircle, IoInformationCircle } from "react-icons/io5";
import type { StatusPillKind } from "../reviewLogic";

/**
 * ReviewStatusPill — the ONE status chip shared by the campaign card, the
 * resource drawer rows and the review-workspace header (PLO-4803). A compact
 * 22px chip (radius component.sm, xs/500) with a leading glyph. Sentence case
 * everywhere. "Overdue" is deliberately NOT a state here — it lives in the due
 * text alongside the pill (see CampaignMeta / OverdueBlip). Revisions are a
 * supplementary flag (RevisionChip) that sits BESIDE this pill, never replacing
 * it. Ports design.tsx `StatusPill` (~lines 199–234).
 */
export interface ReviewStatusPillProps {
  kind: StatusPillKind;
  /** Optional label override; defaults to the per-kind copy. */
  label?: string;
}

const CONFIG: Record<
  StatusPillKind,
  { bg: string; color: string; icon: IconType; label: string }
> = {
  // Not started → muted neutral-grey chip with an outline circle.
  not_started: {
    bg: "bg.chips.subtle",
    color: "text.body.secondary",
    icon: FiCircle,
    label: "Not started",
  },
  // In progress → azurite chip with a clock (ongoing).
  in_progress: {
    bg: "p.50",
    color: "text.body.azurite",
    icon: FiClock,
    label: "In progress",
  },
  // Awaiting admin approval → azurite chip with a filled info glyph.
  awaiting: {
    bg: "p.50",
    color: "text.body.azurite",
    icon: IoInformationCircle,
    label: "Awaiting admin approval",
  },
  // Complete → green chip with a filled success check.
  complete: {
    bg: "success.50",
    color: "success.700",
    icon: IoCheckmarkCircle,
    label: "Complete",
  },
};

const ReviewStatusPill = ({ kind, label }: ReviewStatusPillProps) => {
  const c = CONFIG[kind];
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="2xs"
      h="22px"
      px="2xs"
      borderRadius="component.sm"
      bg={c.bg}
      color={c.color}
      fontFamily="body"
      fontWeight={500}
      fontSize="xs"
      lineHeight="1"
      whiteSpace="nowrap"
      flexShrink={0}
    >
      <Icon as={c.icon} boxSize="12px" aria-hidden />
      <Text as="span">{label ?? c.label}</Text>
    </Box>
  );
};

export default ReviewStatusPill;
